import { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { dashboardService, type FeatureCorrelation } from '../../../services/dashboard.service';
import { Spinner, Warning } from '@phosphor-icons/react';

export const FeatureCorrelationChart = () => {
  const [data, setData] = useState<FeatureCorrelation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await dashboardService.getFeatureCorrelations();
        const reversed = [...res.data].reverse();
        setData(reversed);
      } catch (err: any) {
        setError('Failed to load correlation data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 h-full min-h-[400px] flex items-center justify-center">
        <Spinner size={32} className="animate-spin text-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 h-full min-h-[400px] flex items-center justify-center text-rose-500 font-sans font-medium text-sm gap-2">
        <Warning size={20} /> {error}
      </div>
    );
  }

  const yData = data.map(d => d.feature);
  const seriesData = data.map((d, idx) => {
    const reverseIdx = data.length - 1 - idx; 
    let color = '#cbd5e1'; // slate-300
    if (reverseIdx < 3) {
      color = '#f97316'; // yellow-500
    } else if (reverseIdx < 7) {
      color = '#002673'; // blue-500
    }
    
    return {
      value: Number(d.correlation.toFixed(3)),
      itemStyle: { color }
    };
  });

  const options = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#f1f5f9',
      textStyle: { color: '#334155', fontFamily: 'Inter, sans-serif' },
      padding: [10, 14],
      formatter: (params: any) => {
        const p = params[0];
        return `<div class="font-sans font-medium text-xs text-slate-500 mb-1">${p.name}</div>
                <div class="font-outfit font-bold text-sm text-slate-800">Score: <span style="color:${p.color}">${p.value}</span></div>`;
      }
    },
    grid: {
      left: '0%',
      right: '6%',
      bottom: '0%',
      top: '2%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      name: 'Correlation Strength',
      nameLocation: 'middle',
      nameGap: 24,
      nameTextStyle: {
        fontSize: 10,
        fontFamily: 'Inter, sans-serif',
        color: '#94a3b8',
        fontWeight: 600,
        textTransform: 'uppercase'
      },
      splitLine: { lineStyle: { type: 'dashed', color: '#f1f5f9' } },
      axisLabel: { color: '#94a3b8', fontSize: 10, fontWeight: 500, fontFamily: 'sans-serif' },
      axisLine: { show: false },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'category',
      data: yData,
      axisLabel: {
        fontFamily: 'Inter, sans-serif',
        color: '#475569',
        fontSize: 11,
        fontWeight: 600
      },
      axisLine: { show: false },
      axisTick: { show: false }
    },
    series: [
      {
        type: 'bar',
        data: seriesData,
        label: {
          show: true,
          position: 'right',
          formatter: '{c}',
          fontSize: 10,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          color: '#64748b'
        },
        barWidth: 12,
        itemStyle: {
          borderRadius: [0, 6, 6, 0]
        }
      }
    ]
  };

  return (
    <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100 h-full flex flex-col">
      <div className="mb-6">
        <h2 className="font-outfit font-extrabold text-xl text-slate-900 leading-none mb-1.5">Feature Correlations</h2>
        <p className="text-slate-500 text-sm font-medium">Impact of psychographic traits on donation likelihood.</p>
      </div>
      <div className="flex-1 min-h-[300px]">
        <ReactECharts option={options} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};
